import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Animation frame used to throttle footer collision measurements.
   * @type {number | null}
   */
  #footerLogoFrame = null;

  /** Keeps the desktop logo visible and centers it between the footer divider and its first heading. */
  #updateFooterLogoOffset = () => {
    if (this.#footerLogoFrame) return;

    this.#footerLogoFrame = requestAnimationFrame(() => {
      this.#footerLogoFrame = null;

      const footerUtilities = document.querySelector('[data-testid="footer-utilities"]');
      const utilitiesSection = footerUtilities?.closest('.shopify-section');
      const precedingFooterSection = utilitiesSection?.previousElementSibling;
      const firstFooterText = precedingFooterSection?.querySelector('.text-block');
      const footerSection =
        (precedingFooterSection instanceof HTMLElement ? precedingFooterSection : null) ??
        document.querySelector('.shopify-section-group-footer-group:first-of-type') ??
        document.querySelector('#footer-group .section-wrapper') ??
        footerUtilities;
      if (!(footerSection instanceof HTMLElement)) {
        this.style.removeProperty('--footer-logo-offset');
        return;
      }

      const footerLayout = footerSection.querySelector('.custom-section-content > .layout-panel-flex');

      if (!window.matchMedia('(min-width: 750px)').matches) {
        this.style.removeProperty('--footer-logo-offset');
        if (footerLayout instanceof HTMLElement) footerLayout.style.removeProperty('gap');
        return;
      }

      const viewport = window.visualViewport;
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const viewportBottom = (viewport?.offsetTop ?? 0) + viewportHeight;
      const logoRow = this.querySelector('.header__row--bottom');
      const logoRowHeight = logoRow instanceof HTMLElement ? logoRow.getBoundingClientRect().height : 0;

      if (footerLayout instanceof HTMLElement) {
        footerLayout.style.setProperty('gap', `${logoRowHeight + 12}px`, 'important');
      }

      const footerDivider = footerSection.querySelector('.divider');
      const footerTop =
        footerDivider instanceof HTMLElement
          ? footerDivider.getBoundingClientRect().bottom
          : footerSection.getBoundingClientRect().top;
      const firstFooterTextTop =
        firstFooterText instanceof HTMLElement ? firstFooterText.getBoundingClientRect().top : footerTop;
      const footerIntroGap = Math.max(0, firstFooterTextTop - footerTop);
      const targetTop = footerTop + (footerIntroGap - logoRowHeight) / 2;
      const maximumOffset = Math.max(0, viewportHeight - logoRowHeight);
      const offset = Math.min(maximumOffset, Math.max(0, viewportBottom - targetTop - logoRowHeight));

      this.style.setProperty('--footer-logo-offset', `${offset}px`);
    });
  };

  #observeFooterCollision() {
    if (this.dataset.menuLayout !== 'vertical') return;

    this.#updateFooterLogoOffset();
    document.addEventListener('scroll', this.#updateFooterLogoOffset, { passive: true });
    window.addEventListener('resize', this.#updateFooterLogoOffset);
    window.visualViewport?.addEventListener('resize', this.#updateFooterLogoOffset);
    window.visualViewport?.addEventListener('scroll', this.#updateFooterLogoOffset);
  }

  /** Keeps fixed headers aligned with the mobile browser's visual viewport. */
  #updateVisualViewportOffset = () => {
    const offset = window.visualViewport?.offsetTop ?? 0;
    document.documentElement.style.setProperty('--header-visual-viewport-offset', `${offset}px`);
  };

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#observeFooterCollision();

    const stickyMode = this.getAttribute('sticky');
    const isVerticalAlwaysSticky = stickyMode === 'always' && this.dataset.menuLayout === 'vertical';

    if (isVerticalAlwaysSticky) {
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      this.#updateVisualViewportOffset();
      window.visualViewport?.addEventListener('resize', this.#updateVisualViewportOffset);
      window.visualViewport?.addEventListener('scroll', this.#updateVisualViewportOffset);
      return;
    }

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.visualViewport?.removeEventListener('resize', this.#updateVisualViewportOffset);
    window.visualViewport?.removeEventListener('scroll', this.#updateVisualViewportOffset);
    document.removeEventListener('scroll', this.#updateFooterLogoOffset);
    window.removeEventListener('resize', this.#updateFooterLogoOffset);
    window.visualViewport?.removeEventListener('resize', this.#updateFooterLogoOffset);
    window.visualViewport?.removeEventListener('scroll', this.#updateFooterLogoOffset);
    if (this.#footerLogoFrame) cancelAnimationFrame(this.#footerLogoFrame);
    this.#footerLogoFrame = null;
    this.style.removeProperty('--footer-logo-offset');
    const footerUtilities = document.querySelector('[data-testid="footer-utilities"]');
    const footerSection = footerUtilities?.closest('.shopify-section')?.previousElementSibling;
    const footerLayout = footerSection?.querySelector('.custom-section-content > .layout-panel-flex');
    if (footerLayout instanceof HTMLElement) footerLayout.style.removeProperty('gap');
    document.documentElement.style.removeProperty('--header-visual-viewport-offset');
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
